
import { Device } from "@/hooks/useDevices";
import StatusBadge from "../StatusBadge";
import { getDeviceStatus } from "@/utils/deviceStatus";
import DeviceActions from "./DeviceActions";
import { 
  Calendar, 
  Activity, 
  Heart
} from "lucide-react";
import { AppleDeviceIcon } from "@/components/icons/AppleDeviceIcon";
import { GlassCard, GlassCardContent } from "../ui/glass-card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateDeviceHealthScore, getHealthScoreColor } from "@/utils/deviceHealth";
import { useMemo } from "react";

interface DeviceHeaderProps {
  device: Device;
  onCardClick?: (tab: string) => void;
}

const DeviceHeader = ({ device, onCardClick }: DeviceHeaderProps) => {
  const status = getDeviceStatus(device.last_seen || device.created_at, device.status);
  
  // Calculate health score
  const healthScore = useMemo(() => calculateDeviceHealthScore(device), [device]);
  
  // Query for scheduled actions (script deployments)
  const { data: scriptDeployments = [] } = useQuery({
    queryKey: ['scriptDeployments', device.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('script_deployments')
        .select('*')
        .eq('device_id', device.id)
        .eq('status', 'pending');
      
      if (error) throw error;
      return data || [];
    },
  });
  
  // Count issues needing attention
  const issuesCount = healthScore.metrics.filter(
    m => m.status === 'poor' || m.status === 'fair'
  ).length;

  return (
    <div className="space-y-6">
      {/* Device name and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AppleDeviceIcon 
            osType={device.os_type || 'macOS'} 
            modelName={device.name} 
            className="h-8 w-8 text-gray-600 dark:text-gray-300" 
          />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                {device.name || device.hostname}
              </h1>
              <StatusBadge status={status} />
            </div>
            {device.name !== device.hostname && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {device.hostname}
              </p>
            )}
          </div>
        </div>
        <DeviceActions />
      </div>
      
      {/* Device stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Device Status Card */}
        <GlassCard 
          variant="subtle"
          className="cursor-pointer group"
          onClick={() => onCardClick?.('info')}
        >
          <GlassCardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Device Status</p>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{status.charAt(0).toUpperCase() + status.slice(1)}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Last seen: {new Date(device.last_seen || device.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 backdrop-blur-sm border border-emerald-500/20 group-hover:from-emerald-500/20 group-hover:to-emerald-500/10 transition-all duration-300 flex items-center justify-center">
                <Activity className="h-5 w-5 text-emerald-500" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        
        {/* Scheduled Actions Card */}
        <GlassCard 
          variant="subtle"
          className="cursor-pointer group"
          onClick={() => onCardClick?.('deployments')}
        >
          <GlassCardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Scheduled Actions</p>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{scriptDeployments.length}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Pending deployments
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 backdrop-blur-sm border border-blue-500/20 group-hover:from-blue-500/20 group-hover:to-blue-500/10 transition-all duration-300 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        
        {/* Health Score Card */}
        <GlassCard 
          variant="subtle"
          className="cursor-pointer group"
          onClick={() => onCardClick?.('analytics')}
        >
          <GlassCardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Health Score</p>
                <div className="flex items-baseline gap-2">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{healthScore.overall}</h3>
                  <span className="text-sm font-semibold px-2 py-0.5 rounded" style={{ 
                    backgroundColor: getHealthScoreColor(healthScore.overall) + '20',
                    color: getHealthScoreColor(healthScore.overall)
                  }}>
                    Grade {healthScore.grade}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  {issuesCount > 0 ? `${issuesCount} ${issuesCount === 1 ? 'issue' : 'issues'} need attention` : 'All systems healthy'}
                </p>
              </div>
              <div 
                className="w-8 h-8 rounded-lg backdrop-blur-sm border transition-all duration-300 group-hover:opacity-80 flex items-center justify-center" 
                style={{ 
                  backgroundColor: `${getHealthScoreColor(healthScore.overall)}1A`,
                  borderColor: `${getHealthScoreColor(healthScore.overall)}33`
                }}
              >
                <Heart className="h-5 w-5" style={{ color: getHealthScoreColor(healthScore.overall) }} />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </div>
  );
};

export default DeviceHeader;
